import React from "react";

import type { SelectionOptions } from "features/entries/types/selections";
import type { ProcessedExoticMultiSelection } from "@/hooks/firestore/betting/useEntries";

import { Selection } from "./BaseSelection";
import {
  DiamondIcon as ExoticTitleIcon,
  ExoticIcon as ExoticsLogo,
} from "@picklebet/ui";
import { ComplexBetPill } from "./ComplexBetPill";
import { getSelectionParentName } from "features/entries/util/selection-helpers";
import { useTitle } from "@/hooks/graphql/useTitles";
import { SelectionClamp } from "./SelectionClamp";
import { DrawerLineItem } from "../common/DrawerLineItem";
import { formatUserDateTime } from "utilities/datetime";
import { SELECTION_DATETIME } from "utilities/dateTimeFormats";
import { titleCase } from "title-case";
import pluralize from "pluralize";
import { getSelectionClickHandler } from "features/entries/util/selection-helpers";
import {
  ExoticRunnersInPosition,
  ExoticSelections,
} from "../outcomes/ExoticOutcome";
import { groupRunnersByPosition } from "../../util/selection-helpers";

import * as styles from "./ExoticSelection.module.scss";

interface ExoticSelectionProps {
  entryId: number;
  selection: ProcessedExoticMultiSelection;
  isShared: boolean;
  isPreview?: boolean;
  options: SelectionOptions;
  createdAt: string;
}

const ExoticSelection = ({
  entryId,
  isShared,
  selection,
  isPreview = false,
  options,
  createdAt,
}: ExoticSelectionProps) => {
  const parentName = getSelectionParentName(selection);
  const title = useTitle(selection.event.sport);

  const combos = selection.exoticCombinations ?? 0;

  return (
    <Selection
      onClick={getSelectionClickHandler(selection)}
      isCashedOut={options.isCashedOut}
      isFirst={options.isFirst}
      isLast={options.isLast}
      isVoid={options.isVoid}
      isSelectionWon={options.isSelectionWon}
      isEntryWon={options.isEntryWon}
      isCombo={options.isCombo}
      isDecided={options.isDecided}
      connectUp={options.connectUp}
      connectDown={options.connectDown}
      slots={{
        icon: <ExoticTitleIcon className={styles.diamondIcon} />,
        outcomeName: <span className={styles.outcomeName}>{parentName}</span>,
        marketName: `${title?.name} ${selection.event.attributes?.matchMode ?? ""}`,
        clamp: (
          <SelectionClamp
            selection={selection}
            isPreview={isPreview}
            isEntryWon={options.isEntryWon}
          />
        ),
        drawerContent: (
          <>
            <DrawerLineItem
              label="Start"
              value={formatUserDateTime(
                selection.event.scheduledStartTime!,
                SELECTION_DATETIME,
              )}
            />

            <DrawerLineItem
              label="Event"
              value={titleCase(selection.event.eventName?.toLowerCase() ?? "")}
            />

            <DrawerLineItem label="" value={parentName} />

            <DrawerLineItem
              label="Placed"
              value={formatUserDateTime(createdAt, SELECTION_DATETIME)}
            />

            {!isShared && (
              <DrawerLineItem label="Entry Id" value={`B-${entryId}`} />
            )}
          </>
        ),
      }}
    >
      <ComplexBetPill
        icon={<ExoticsLogo />}
        odds={pluralize("Combo", combos, true)}
        isWon={options.isEntryWon}
      />

      <ExoticSelections className={styles.exoticOutcomes}>
        {groupRunnersByPosition(selection.outcomes).map((runners, position) => {
          return (
            <ExoticRunnersInPosition
              isAnimated={false}
              key={`exotic-runners-position-${position}`}
              position={position}
              outcomes={runners}
            />
          );
        })}
      </ExoticSelections>
    </Selection>
  );
};

export { ExoticSelection };
